import React from 'react';
import {ChevronDownIcon, ArrowPathIcon} from '@heroicons/react/24/solid';
import styles from './Table.module.css';
import {DateRangeFilterProps} from './DateRangeFilter';

export interface Filter {
    key: string;
    label: string;
    options?: { label: string; value: string; }[];
    multiple?: boolean;
    value?: any;
    component?: React.ReactElement<DateRangeFilterProps>;
    displayMap?: Record<string, string>;
}

type FilterWithToggle = Omit<Filter, 'component'> & {
    component?: React.ReactElement<DateRangeFilterProps>;
    isOpen?: boolean;
    onToggle?: () => void;
};

interface FilterSectionProps {
    filters: FilterWithToggle[];
    filterValues: Record<string, any>;
    onFilterChange: (key: string, value: any) => void;
    onReset?: () => void;
}

//Table 컴포넌트 필터
const FilterSection: React.FC<FilterSectionProps> = ({
                                                         filters,
                                                         filterValues,
                                                         onFilterChange,
                                                         onReset
                                                     }) => {
    const getDisplayValue = (filter: FilterWithToggle) => {
        //다중 선택
        if (filter.multiple) {
            const selectedValues = filterValues[filter.key] as string[];
            if (!selectedValues?.length) return '없음';
            if (filter.options?.length === selectedValues?.length) return "전체";

            //선택된 값 출력 형식
            const displayValues = selectedValues.map(value => {
                if (filter.displayMap && filter.displayMap[value]) {
                    return filter.displayMap[value];
                }
                return filter.options?.find(opt => opt.value === value)?.label || value;
            });
            //4개 이상 선택된 경우 4번째 값부터는 '...'으로 출력
            if (displayValues.length > 4) {
                return `${displayValues[0]}, ${displayValues[1]}, ${displayValues[2]}, ${displayValues[3]}, ...`;
            }
            return displayValues.join(', ');
        }
        //단일 선택
        if (filter.options) {
            return filter.options.find(opt => opt.value === filterValues[filter.key])?.label || '전체';
        }

        return filterValues[filter.key] || '없음';
    };

    //필터 클릭
    const handleFilterOptionClick = (filter: FilterWithToggle, optionValue: string) => {
        if (filter.multiple) {
            const currentValues = filterValues[filter.key] as (string | number)[];
            const typedOptionValue = filter.key === 'clientIds' ? parseInt(optionValue) : optionValue;
            const newValues = currentValues.includes(typedOptionValue)
                ? currentValues.filter(value => String(value) !== String(typedOptionValue))
                : [...currentValues, typedOptionValue];

            onFilterChange(filter.key, newValues);
        } else {
            onFilterChange(filter.key, optionValue);
            filter.onToggle?.();
        }
    };

    // 선택된 옵션 표시
    const isOptionSelected = (filter: FilterWithToggle, optionValue: string): boolean => {
        if (filter.multiple) {
            const selectedValues = filterValues[filter.key] as (string | number)[] || [];
            return selectedValues.map(String).includes(optionValue);
        }
        return filterValues[filter.key] === optionValue;
    };

    return (
        <div className={styles.filterSection}>
            <div className={styles.header}>
                <span className={styles.filterLabel}>Filter</span>
                {onReset && (
                    <button className={styles.mobileAllResetButton} onClick={onReset}>
                        <ArrowPathIcon className={styles.icon}/>
                    </button>
                )}
            </div>
            {/*필터*/}
            {filters.map((filter) => (
                <div key={filter.key} className={styles.filterGroup}>
                    {filter.component ? (
                        React.cloneElement<Partial<DateRangeFilterProps>>(
                            filter.component,
                            {
                                label: filter.component.props.label,
                                startDate: filter.component.props.startDate,
                                endDate: filter.component.props.endDate,
                                onDateChange: filter.component.props.onDateChange,
                                isOpen: filter.isOpen,
                                onToggle: filter.onToggle,
                            }
                        )
                    ) : (
                        <>
                            <span className={styles.filterText}>{filter.label}:</span>
                            {/*필터창 버튼*/}
                            <button
                                className={styles.filterButton}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    filter.onToggle?.();
                                }}
                            >
                                <span>{getDisplayValue(filter)}</span>
                                <ChevronDownIcon
                                    className={`w-4 h-4 transition-transform ${
                                        filter.isOpen ? 'rotate-180' : ''
                                    }`}
                                />
                            </button>
                            {/*필터창*/}
                            {filter.isOpen && filter.options && (
                                // 필터 아이템 드롭다운 표시
                                <div className={styles.filterDropdown}>
                                    {filter.options.map((option) => (
                                        <button
                                            key={option.value}
                                            className={`${styles.filterOption} ${
                                                isOptionSelected(filter, option.value)
                                                    ? styles.filterOptionActive
                                                    : ''
                                            }`}
                                            onClick={() => handleFilterOptionClick(filter, option.value)}
                                        >
                                            {filter.multiple && (
                                                <span className={styles.checkbox}>
                                                   {isOptionSelected(filter, option.value) && '✓'}
                                               </span>
                                            )}
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            ))}
            {onReset && (
                <button className={styles.allResetButton} onClick={onReset}>
                    <ArrowPathIcon className={styles.icon}/>
                </button>
            )}
        </div>
    );
};

export default FilterSection;